import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";

interface User {
  userID: string;
  email: string;
  firstName: string;
  lastName: string;
  roleID: number;
  roleName: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  checkAuth: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const checkAuth = async () => {
    const response = await fetch("/auth/me").then((r) => r.json());

    if (response === null) {
      setIsLoading(false);

      return window.location.assign("/sign-in");
    } else {
      setIsAuthenticated(true);
      setUser({
        email: response.email as string,
        firstName: "TEST",
        lastName: "USER",
        userID: response.id as string,
        roleID: 0,
        roleName: "test",
      });

      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const logout = () => {
    setIsAuthenticated(false);
    window.location.assign("/sign-out");
  };

  const value: AuthContextType = {
    logout,
    user,
    isAuthenticated,
    isLoading,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
